export const ADDRESS_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;//7 days in milliseconds
export const ADDRESS_CACHE_STORAGE_KEY = 'schedule.address-cache.v1';

const normalizeAddressPart = value =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

let cachedUserCountry = null;

export const getUserCountry = () => {
  if (cachedUserCountry) {
    return cachedUserCountry;
  }

  try {
    if (typeof window !== 'undefined') {
      const meString = window.sessionStorage.getItem('me');
      if (meString) {
        const me = JSON.parse(meString);
        cachedUserCountry = me?.profile?.country || 'BR';
        return cachedUserCountry;
      }
    }
  } catch {
    // fallback
  }
  return 'BR';
};

export const normalizeAddress = address =>
  [address?.country || getUserCountry(), address?.zipcode, address?.street, address?.number, address?.city, address?.state]
    .map(normalizeAddressPart)
    .join('|');

const getWebCrypto = () => {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    return window.crypto;
  }

  if (globalThis.crypto?.subtle) {
    return globalThis.crypto;
  }

  throw new Error('Web Crypto API is not available');
};

const hashCacheMap = new Map();

export const generateAddressHash = async address => {
  const normalizedAddress = normalizeAddress(address);

  if (hashCacheMap.has(normalizedAddress)) {
    return hashCacheMap.get(normalizedAddress);
  }

  const encodedAddress = new TextEncoder().encode(normalizedAddress);
  const hashBuffer = await getWebCrypto().subtle.digest('SHA-256', encodedAddress);
  const hash = Array.from(new Uint8Array(hashBuffer))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

  if (hashCacheMap.size > 2000) {
    hashCacheMap.clear();
  }
  hashCacheMap.set(normalizedAddress, hash);

  return hash;
};

const getLocalStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const normalizeCoordinate = coordinate => {
  if (coordinate === null || coordinate === undefined || coordinate === '') return null;

  const parsedCoordinate = Number(coordinate);

  return Number.isFinite(parsedCoordinate) ? parsedCoordinate : null;
};

const isValidCoordinate = coordinate => coordinate === null || Number.isFinite(coordinate);

const isValidCacheEntry = entry =>
  entry &&
  typeof entry === 'object' &&
  typeof entry.address_hash === 'string' &&
  Number.isFinite(entry.expiresAt) &&
  isValidCoordinate(entry.latitude) &&
  isValidCoordinate(entry.longitude);

export class AddressCacheService {
  #storage;
  #storageKey;
  #ttlMs;
  #now;

  constructor({
    storage = getLocalStorage(),
    storageKey = ADDRESS_CACHE_STORAGE_KEY,
    ttlMs = ADDRESS_CACHE_TTL_MS,
    now = Date.now,
  } = {}) {
    this.#storage = storage;
    this.#storageKey = storageKey;
    this.#ttlMs = ttlMs;
    this.#now = now;
  }

  #read() {
    if (!this.#storage) return {};

    try {
      const serializedCache = this.#storage.getItem(this.#storageKey);

      if (!serializedCache) return {};

      const cache = JSON.parse(serializedCache);

      if (!cache || typeof cache !== 'object' || Array.isArray(cache)) {
        throw new Error('Invalid address cache');
      }

      return cache;
    } catch {
      try {
        this.#storage.removeItem(this.#storageKey);
      } catch {
        // Storage can be unavailable or blocked by the browser.
      }

      return {};
    }
  }

  #write(cache) {
    if (!this.#storage) return;

    try {
      if (Object.keys(cache).length) {
        this.#storage.setItem(this.#storageKey, JSON.stringify(cache));
      } else {
        this.#storage.removeItem(this.#storageKey);
      }
    } catch {
      // Cache failures must not prevent geocoding or map rendering.
    }
  }

  #removeInvalidOrExpired(cache) {
    const currentTime = this.#now();
    let hasChanges = false;

    Object.entries(cache).forEach(([hash, entry]) => {
      if (!isValidCacheEntry(entry) || entry.expiresAt <= currentTime) {
        delete cache[hash];
        hasChanges = true;
      }
    });

    if (hasChanges) this.#write(cache);

    return cache;
  }

  #createEntry(addressHash, coordinates) {
    const cachedAt = this.#now();

    return {
      address_hash: addressHash,
      latitude: normalizeCoordinate(coordinates?.latitude),
      longitude: normalizeCoordinate(coordinates?.longitude),
      source: coordinates?.source ?? null,
      cached: coordinates?.cached ?? false,
      cachedAt,
      expiresAt: cachedAt + this.#ttlMs,
    };
  }

  async get(address) {
    const addressHash = await generateAddressHash(address);
    const cache = this.#removeInvalidOrExpired(this.#read());
    const entry = cache[addressHash];

    return entry ? { ...entry } : null;
  }

  async getMany(addresses) {
    const addressHashes = await Promise.all(addresses.map(generateAddressHash));
    const cache = this.#removeInvalidOrExpired(this.#read());

    return addressHashes.map(addressHash => {
      const entry = cache[addressHash];

      return entry ? { ...entry } : null;
    });
  }

  async set(address, coordinates) {
    const addressHash = await generateAddressHash(address);
    const cache = this.#removeInvalidOrExpired(this.#read());
    const entry = this.#createEntry(addressHash, coordinates);

    cache[addressHash] = entry;
    this.#write(cache);

    return { ...entry };
  }

  async setMany(entries) {
    const addressHashes = await Promise.all(entries.map(entry => generateAddressHash(entry.address)));
    const cache = this.#removeInvalidOrExpired(this.#read());
    const cachedEntries = entries.map((entry, index) => {
      const cacheEntry = this.#createEntry(addressHashes[index], entry.coordinates);

      cache[addressHashes[index]] = cacheEntry;

      return { ...cacheEntry };
    });

    this.#write(cache);

    return cachedEntries;
  }

  async remove(address) {
    const addressHash = await generateAddressHash(address);
    const cache = this.#read();

    if (!(addressHash in cache)) return false;

    delete cache[addressHash];
    this.#write(cache);

    return true;
  }

  clearExpired() {
    const cache = this.#read();
    const entriesBeforeCleanup = Object.keys(cache).length;
    const validCache = this.#removeInvalidOrExpired(cache);

    return entriesBeforeCleanup - Object.keys(validCache).length;
  }

  clear() {
    if (!this.#storage) return;

    try {
      this.#storage.removeItem(this.#storageKey);
    } catch {
      // Storage can be unavailable or blocked by the browser.
    }
  }
}

const addressCacheService = new AddressCacheService();

export default addressCacheService;
